import { useMemo, useState } from "react";
import InputModal from "../../components/modals/InputModal";
import WithAuth from "../../hoc/WithAuth";
import { useInputList } from "../../api/input";
import { useTablePrint } from "../../hooks/useTablePrint";
import { ExportButton } from "../../components/ui/ExportButton";
import { AppButton } from "../../components/ui/AppButton";
import { Icons } from "../../assets/images/icons";
import { DataTable } from "../../components/ui/data-table";
import { InputType } from "../../types/input";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";

function InputPage() {
  /** State */
  const [searchText, setSearchText] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [selectedInput, setSelectedInput] = useState<any>();

  /** API */
  const { data: inputs, isLoading } = useInputList({
    search: searchText || undefined,
    page: currentPage - 1,
    size: pageSize,
  });

  const { handlePrint } = useTablePrint();

  /** Transform inputs for table */
  const transformedInputs = useMemo((): InputType[] => {
    if (!inputs?.data?.content) return [];
    return inputs.data.content.map((item: InputType) => ({
      id: item.id,
      category: item.category || "N/A",
      unitOfMeasurementId: item.unitOfMeasurementId,
      unitOfMeasurementName: item.unitOfMeasurementName || "N/A",
      name: item.name,
      description: item.description,
    }));
  }, [inputs]);

  /** Handlers */
  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleCreate = () => {
    setOpen(true);
  };

  const hideModal = () => {
    setSelectedInput(undefined);
    setSelectedId("");
    setOpen(false);
  };

  /** Columns */
  const inputColumns = [
    {
      key: "name",
      title: "Name",
      dataIndex: "name",
      align: "left" as const,
    },
    {
      key: "category",
      title: "Category",
      dataIndex: "category",
      align: "left" as const,
    },
    {
      key: "unitOfMeasurementName",
      title: "Unit",
      dataIndex: "unitOfMeasurementName",
      align: "left" as const,
    },
    {
      key: "actions",
      title: "Actions",
      align: "center" as const,
      render: (id: string, record: InputType) => {
        return (
          <Button
            variant="default"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedInput(record);
              setSelectedId(record.id);
              setOpen(true);
            }}
          >
            <img
              src={Icons.pencilEdit}
              alt="edit"
              className="h-4 w-4 text-gray-100 mr-1"
            />
            Edit
          </Button>
        );
      },
    },
  ];

  return (
    <div
      className="bg-transparent lg:bg-white"
      style={{ borderRadius: 8.5, marginTop: 25 }}
    >
      <div className="flex flex-col sm:flex-row justify-between">
        <div className="relative w-full lg:w-64">
          <Input
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search input products"
            className="pl-10 rounded-2xl w-[308px] h-11"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            <img src={Icons.search} alt="search" className="w-6 h-6" />
          </div>
        </div>

        <div className="flex gap-2">
          <AppButton
            customIcon={Icons.printIcon}
            variant="outline"
            onClick={() =>
              handlePrint(inputColumns, transformedInputs, "Input Report")
            }
            className="bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
          >
            Print
          </AppButton>

          <ExportButton
            columns={inputColumns}
            data={transformedInputs}
            fileName="inputs"
            title="Lookup Report"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: inputs?.data?.totalElements ?? 0,
            }}
            customIcon={Icons.fileExport}
            className="bg-[#DDDDDD] hover:bg-gray-300"
          />

          <AppButton
            customIcon={Icons.addCircle}
            variant="success"
            onClick={handleCreate}
          >
            Create New
          </AppButton>
        </div>
      </div>

      {/** Desktop Table */}
      <div className="hidden md:block">
        <DataTable<InputType>
          data={transformedInputs}
          columns={inputColumns}
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: inputs?.data?.totalElements ?? 0,
            onChange: handlePaginationChange,
          }}
          emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
          emptyText="No inputs found"
          rowKey="id"
        />
      </div>

      <InputModal
        open={open}
        selectedId={selectedId}
        selectedInput={selectedInput}
        hideModal={hideModal}
      />
    </div>
  );
}

export default WithAuth(InputPage);
